import * as React from 'react';
import { Currency } from 'core/utils';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { Res, Permission, VatDTO, PreparationTypeDTO, PreparationOrderDTO, ProductPrintMode, ColorDTO, ProductForSaleDTO, SizeDTO, ProductOrigin } from 'core/api';
import { BackActionButton, Content, Footer, Header, PhoneOnly, PosScreen } from 'pos/ui';
import { RestaurantFacade } from 'pos/facades';
import { Modal } from 'core/ui-next/modal';
import { Box, VStack, Text } from '@chakra-ui/react';
import { BasicButton } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { zMonetary } from 'core/infrastructure-next';

type Props = {
	existingVats: VatDTO[];
	existingPreparationTypes: PreparationTypeDTO[];
	existingPreparationOrders: PreparationOrderDTO[];
} & Modal.Actions<ProductForSaleDTO>;

const stateSchema = z.object({
	name: z.string().min(1, { message: Res.Msg.MSG_PRODUCT_NAME_IS_REQUIRED() }),
	price: zMonetary({ nonNegative: true, required: true }),
	ticketType: z.string(),
	vat: z.object({
		Id: z.number(),
		SurchargePercentage: z.number(),
		VatPercentage: z.number(),
		Name: z.string()
	}).nullable(),
	preparationType: z.object({
		Id: z.number(),
		Name: z.string()
	}),
	preparationOrder: z.object({
		Id: z.number(),
		Name: z.string(),
		Priority: z.number()
	})
}).refine((data) => {
	if (data.vat === null) {
		return false;
	}
	return true;
}, { message: Res.Msg.MSG_PRODUCT_VAT_IS_REQUIRED(), path: ['vat'] }
).refine((data) => {
	if (data.preparationType.Id !== 0 && data.preparationOrder.Id === 0) {
		return false;
	}
	return true;
}, { message: Res.Msg.MSG_PRODUCT_WITH_PREPARATION_TYPE_MUST_HAVE_A_PREPARATION_ORDER(), path: ['preparationOrder'] });

type State = z.infer<typeof stateSchema>;

function ComposeUnusualProduct(props: Props) {
	const { existingVats, existingPreparationTypes, existingPreparationOrders, cancel, close } = props;
	const { Form, Input, DecimalInput, Select } = useControlsFor<State>();
	const formRef = React.useRef<FormRef<State>>(null);

	const initialState: State = {
		name: '',
		price: 0,
		ticketType: SalesStore.getTicket().VatIncluded ? Res.Web.COMPOSE_UNUSUAL_PRODUCT_VAT_INCLUDED_LBL() : Res.Web.COMPOSE_UNUSUAL_PRODUCT_VAT_NOT_INCLUDED_LBL(),
		vat: null,
		preparationType: existingPreparationTypes[0],
		preparationOrder: existingPreparationOrders[0]
	};

	const onOK = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		close({
			AdjustIngredientsOnSale: false,
			AskForAddins: false,
			AskForPreparationNotes: false,
			Barcode: '',
			CannotBeSold: false,
			CannotBeSoldReason: '',
			CouponCode: '',
			FamilyName: '',
			HasAddins: false,
			HasFixedPrice: true,
			HasLotControlOnSale: false,
			HasMoreFormats: false,
			Id: 0,
			IsAvailable: true,
			IsMenu: false,
			IsSoldBySizeAndColor: false,
			IsSoldByWeight: false,
			IsSubCategory: false,
			IsSubFamily: false,
			Name: result.value.name,
			Origin: ProductOrigin.Inventory,
			PreparationOrderId: result.value.preparationOrder.Id,
			PreparationTypeId: result.value.preparationType.Id,
			Price: result.value.price,
			PriceListId: SalesStore.getTicket().PriceList.Id,
			PrintMode: ProductPrintMode.Always,
			RequiresCouponCode: false,
			SaleFormatId: 0,
			SaleFormatName: result.value.name,
			SaleFormatRatio: 1,
			Priority: 0,
			Style: {
				BackColor: '',
				ImageId: '',
				Text: ''
			},
			SurchargeRate: result.value.vat!.SurchargePercentage / 100,
			VatId: result.value.vat!.Id,
			VatRate: result.value.vat!.VatPercentage / 100,
			PriceLookUpCode: ''
		});
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return (
		<PosScreen screenId={'COMPOSE_UNUSUAL_PRODUCT'} size='small' useKeyPad={true}>
			<Header title={Res.Web.COMPOSE_UNUSUAL_PRODUCT()} onClose={cancel} />
			<Content>
				<VStack width={'full'} height={'full'} padding={'6'} gap={'6'} overflow={'auto'}>
					<VStack width={'full'}>
						<Text fontSize={'md'} fontWeight={'semibold'}>
							{Res.Web.COMPOSE_UNUSUAL_PRODUCT_TITLE()}
						</Text>
					</VStack>
					<Box width={'full'}>
						<Form ref={formRef} initialValue={initialState} schema={stateSchema}>
							<VStack width={'full'} gap={'4'}>
								<Input linkTo={'name'} label={Res.Web.COMPOSE_UNUSUAL_PRODUCT_DESCRIPTION_LBL()} placeholder={Res.Web.COMPOSE_UNUSUAL_PRODUCT_DESCRIPTION_PLACEHOLDER()} className='disable-keypad' maxLength={120} required />
								<DecimalInput linkTo={'price'} label={Res.Web.COMPOSE_UNUSUAL_PRODUCT_PRICE_LBL()} placeholder={Res.Web.COMPOSE_UNUSUAL_PRODUCT_PRICE_WITH_CURRENCY_SYMBOL_PLACEHOLDER(Currency.SYMBOL)} allowNegative={false} required />
								<Select linkTo={'vat'} label={Res.Web.COMPOSE_UNUSUAL_PRODUCT_VAT_TYPE_LBL()} options={existingVats} renderOption={opt => opt?.Name ? opt.Name : ''} valueGetter={opt => opt?.Name ? opt.Name : ''} placeholder={Res.Web.COMPOSE_UNUSUAL_PRODUCT_VAT_TYPE_PLACEHOLDER()} required />
								<Select linkTo={'preparationType'} label={Res.Web.COMPOSE_UNUSUAL_PRODUCT_PREPARATION_TYPE_LBL()} options={existingPreparationTypes} renderOption={opt => opt.Name} valueGetter={opt => opt.Name} placeholder={Res.Web.COMPOSE_UNUSUAL_PRODUCT_PREPARATION_TYPE_PLACEHOLDER()} required />
								<Select linkTo={'preparationOrder'} label={Res.Web.COMPOSE_UNUSUAL_PRODUCT_PREPARATION_ORDER_LBL()} options={existingPreparationOrders} renderOption={opt => opt.Name} valueGetter={opt => opt.Name} placeholder={Res.Web.COMPOSE_UNUSUAL_PRODUCT_PREPARATION_ORDER_PLACEHOLDER()} required />
							</VStack>
						</Form>
					</Box>
					<BasicButton type='success' width={'full'} text={Res.Common.OK_BTN()} onClick={onOK} />
				</VStack>
			</Content>
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
				</PhoneOnly>
			</Footer>
		</PosScreen>
	);
}

declare global {
	interface CommandType {
		composeUnusualProduct: (onProductAdded: (product: ProductForSaleDTO, quantity?: number, size?: SizeDTO, color?: ColorDTO) => Promise<Result>) => Promise<Result>
	}
}

Command.register('composeUnusualProduct', Preconditions.ensure(Permission.Ticket_ComposeUnusualProduct), async (onProductAdded: (product: ProductForSaleDTO, quantity?: number, size?: SizeDTO, color?: ColorDTO) => Promise<Result>): Promise<Result> => {

	const [vats, preparationTypes, preparationOrders] = await Promise.all([
		RestaurantFacade.getVats(),
		RestaurantFacade.getPreparationTypes(),
		RestaurantFacade.getPreparationOrders()
	]);

	const preparationTypesWithNull = [{
		Id: 0,
		Name: Res.Web.COMPOSE_UNUSUAL_PRODUCT_NO_TYPE()
	}, ...preparationTypes];

	const preparationOrdersWithNull = [{
		Id: 0,
		Name: Res.Web.COMPOSE_UNUSUAL_PRODUCT_NO_ORDER(),
		Priority: 0
	}, ...preparationOrders];

	const result = await Modal.show(ComposeUnusualProduct, { existingVats: vats, existingPreparationTypes: preparationTypesWithNull, existingPreparationOrders: preparationOrdersWithNull });

	if (!result.ok)
		return Result.error();

	return onProductAdded(result.value, 1);
});